/* eslint-disable no-console */
import { create, StoreApi, UseBoundStore } from 'zustand';
import { toast } from 'react-toastify';
import getErrorMessage from '../utils/error';
import { IPaginationData, IParamsSearch, IReceipt, IReceiptDetail, IResponse } from '../types/types';
import {
  createReceipt,
  deleteReceipt,
  getAllReceipt,
  getAllReceiptDetail,
  getReceiptById,
  updateReceipt,
} from '../utils/api';

interface IUseReceipt {
  loading: boolean;
  receipt: IReceipt | null;
  receipts: IReceipt[] | null;
  receiptDetails: IReceiptDetail[] | null;
  paginationData: IPaginationData | null;
  abortController: AbortController | null;
  cancelFetchReceipt: () => void;
  fetchReceipt: (params?: IParamsSearch) => Promise<IResponse>;
  fetchReceiptDetail: (id: string) => Promise<IResponse>;
  getReceipt: (id: string) => Promise<IResponse>;
  deleteReceipt: (id: string) => Promise<IResponse>;
  createReceipt: (data: IReceipt) => Promise<IResponse>;
  updateReceipt: (id: string, data: IReceipt) => Promise<IResponse>;
  resetReceipt: () => void;
  resetReceipts: () => void;
  resetReceiptDetails: () => void;
}

const useReceipt: UseBoundStore<StoreApi<IUseReceipt>> = create((set, get) => ({
  loading: false,
  receipt: null,
  receipts: null,
  receiptDetails: null,
  paginationData: null,
  abortController: null,
  cancelFetchReceipt: () => {
    const { abortController } = get();
    if (abortController && !abortController.signal.aborted) {
      abortController.abort();
      set({ loading: false, abortController: null });
    }
  },
  fetchReceipt: async (params = {}) => {
    const newAbortController = new AbortController();

    try {
      set(() => ({ loading: true }));

      if (get().abortController) {
        get().abortController?.abort();
      }

      set(() => ({ abortController: newAbortController }));

      // Construct query parameters
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

      // Add filters
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) queryParams.append(`${key}`, value.toString());
        });
      }

      // Add sorting
      if (params.sorting) {
        queryParams.append('sortBy', params.sorting.field);
        queryParams.append('sortOrder', params.sorting.order);
      }

      const { success, message, data } = await getAllReceipt(queryParams.toString(), newAbortController.signal);

      // Only update state if this request wasn't aborted
      if (!newAbortController.signal.aborted) {
        if (!success) {
          throw new Error(message);
        }

        set(() => ({
          receipts: data.data,
          paginationData: data.pagination,
        }));
      }

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Request dibatalkan');
        return {
          success: false,
          message: 'Request dibatalkan',
        };
      }

      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      const currentController = get().abortController;
      if (currentController === newAbortController && !currentController?.signal.aborted) {
        set(() => ({ loading: false }));
      }
    }
  },
  fetchReceiptDetail: async (id) => {
    try {
      set(() => ({ loading: true }));
      const { success, message, data } = await getAllReceiptDetail(id);

      if (!success) {
        throw new Error(message);
      }

      set(() => ({ receiptDetails: data }));

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  getReceipt: async (id) => {
    try {
      set(() => ({ loading: true }));
      const { success, message, data } = await getReceiptById(id);

      if (!success) {
        throw new Error(message);
      }

      set(() => ({ receipt: data }));

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  deleteReceipt: async (id) => {
    const createToast = toast.loading('Delete receipt...');
    try {
      set(() => ({ loading: true }));

      const prevReceipts = get().receipts;

      const { success, message } = await deleteReceipt(id);

      if (!success) {
        throw new Error(message);
      }

      set({ receipts: prevReceipts?.filter((receipt) => receipt.receipt_id !== id) });

      toast.update(createToast, {
        position: 'top-right',
        render: 'Delete receipt successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      // Rollback jika error terjadi
      set({ receipts: get().receipts });

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  createReceipt: async (data) => {
    const createToast = toast.loading('Create receipt...');
    try {
      set(() => ({ loading: true }));
      const { success, message } = await createReceipt(data);

      if (!success) {
        throw new Error(message);
      }

      toast.update(createToast, {
        position: 'top-right',
        render: 'Create receipt successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  updateReceipt: async (id, data) => {
    const createToast = toast.loading('Update receipt...');

    try {
      set(() => ({ loading: true }));
      const { success, message } = await updateReceipt(id, data);

      if (!success) {
        throw new Error(message);
      }

      toast.update(createToast, {
        position: 'top-right',
        render: 'Update receipt successfully',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: true,
        message,
      };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);

      toast.update(createToast, {
        position: 'top-right',
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      set(() => ({ loading: false }));
    }
  },
  resetReceipt: () => set({ receipt: null }),
  resetReceipts: () => set({ receipts: null }),
  resetReceiptDetails: () => set({ receiptDetails: null }),
}));

// Subscribe untuk mengecek perubahan state
// eslint-disable-next-line @typescript-eslint/no-unused-vars
// const unsubscribe = useReceipt.subscribe((state) => {
//   console.log('State receipt:', state);
// });

export default useReceipt;
